import { cacheService } from './cacheService';
import { supabase } from '../lib/supabase';
import { clinicCacheService } from './clinicCacheService';
import { safeLog } from '../utils/safeLog';

export interface CachedBillConfig {
  id: string;
  config_type: string;
  clinic_id?: string;
  system_prompt: string;
  personality_type: string;
  clinic_info?: string;
  knowledge_base?: string;
  sobriety_congrats: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

class BillConfigCacheService {
  private readonly CONFIG_KEY_PREFIX = 'bill_config:';

  /**
   * Get Bill configuration for a specific user (resolves clinic hierarchy)
   */
  async getBillConfigForUser(userId: string): Promise<CachedBillConfig | null> {
    const key = `${this.CONFIG_KEY_PREFIX}user:${userId}`;
    
    try {
      return await cacheService.get(
        key,
        async () => {
          safeLog.debug(`🤖 BillConfigCacheService: Fetching Bill config for user: ${userId.substring(0, 8)}...`);
          
          // Use clinicCacheService to get user's clinic data (avoids profile lookup duplication)
          const clinicData = await clinicCacheService.getUserClinicData();
          
          let billConfig: CachedBillConfig | null = null;

          // Try to get clinic-specific configuration first
          if (clinicData?.id) {
            safeLog.debug(`🏥 BillConfigCacheService: Looking for clinic-specific Bill config: ${clinicData.id}`);
            
            const { data: clinicConfig, error: clinicError } = await supabase
              .from('bill_config')
              .select('*')
              .eq('clinic_id', clinicData.id)
              .eq('config_type', 'main')
              .single();

            if (!clinicError && clinicConfig) {
              billConfig = clinicConfig as CachedBillConfig;
              safeLog.debug('✅ BillConfigCacheService: Found clinic-specific Bill config:', {
                clinic: clinicData.name,
                personality: clinicConfig.personality_type
              });
            } else if (clinicError && clinicError.code !== 'PGRST116') {
              safeLog.warn('⚠️ BillConfigCacheService: Error fetching clinic Bill config (will use defaults):', {
                error: clinicError.message,
                clinicId: clinicData.id
              });
            }
          } else {
            safeLog.debug('ℹ️ BillConfigCacheService: No clinic data available, using admin defaults');
          }

          // Fallback to admin default configuration
          if (!billConfig) {
            safeLog.debug('🔧 BillConfigCacheService: Using admin default Bill config');
            
            const { data: defaultConfig, error: defaultError } = await supabase
              .from('bill_config')
              .select('*')
              .is('clinic_id', null)
              .eq('config_type', 'main')
              .single();

            if (defaultError) {
              safeLog.error('❌ BillConfigCacheService: Error fetching default Bill config:', {
                error: defaultError.message,
                code: defaultError.code
              });
              throw new Error(`Failed to fetch default Bill config: ${defaultError.message}`);
            }

            billConfig = defaultConfig as CachedBillConfig;
            safeLog.debug('✅ BillConfigCacheService: Using admin default configuration');
          }

          if (!billConfig) {
            throw new Error('No Bill configuration found');
          }

          safeLog.debug('✅ BillConfigCacheService: Bill config resolved successfully:', {
            type: billConfig.config_type,
            personality: billConfig.personality_type,
            hasClinicInfo: !!billConfig.clinic_info,
            hasKnowledgeBase: !!billConfig.knowledge_base,
            isClinicSpecific: !!billConfig.clinic_id
          });

          return billConfig;
        },
        cacheService.getConfig('BILL_CONFIG')
      );
    } catch (error) {
      safeLog.error(`❌ BillConfigCacheService: Error getting config for user ${userId.substring(0, 8)}...:`, {
        error: error instanceof Error ? error.message : String(error),
        timestamp: new Date().toISOString()
      });
      return null;
    }
  }

  /**
   * Get admin default Bill configuration
   */
  async getAdminDefaultConfig(): Promise<CachedBillConfig | null> {
    const key = `${this.CONFIG_KEY_PREFIX}admin:default`;
    
    try {
      return await cacheService.get(
        key,
        async () => {
          safeLog.debug('🔧 Fetching admin default Bill config');
          
          const { data, error } = await supabase
            .from('bill_config')
            .select('*')
            .is('clinic_id', null)
            .eq('config_type', 'main')
            .single();

          if (error) {
            safeLog.error('❌ Error fetching admin default Bill config:', error);
            throw new Error(`Failed to fetch admin default config: ${error.message}`);
          }

          if (!data) {
            throw new Error('No admin default configuration found');
          }

          safeLog.debug('✅ Admin default Bill config fetched successfully');
          return data as CachedBillConfig;
        },
        cacheService.getConfig('BILL_CONFIG')
      );
    } catch (error) {
      safeLog.error('❌ BillConfigCacheService: Error getting admin default config:', error);
      return null;
    }
  }

  /**
   * Get clinic-specific Bill configuration
   */
  async getClinicConfig(clinicId: string): Promise<CachedBillConfig | null> {
    const key = `${this.CONFIG_KEY_PREFIX}clinic:${clinicId}`;
    
    try {
      return await cacheService.get(
        key,
        async () => {
          safeLog.debug(`🏥 Fetching Bill config for clinic: ${clinicId}`);
          
          const { data, error } = await supabase
            .from('bill_config')
            .select('*')
            .eq('clinic_id', clinicId)
            .eq('config_type', 'main')
            .single();

          if (error) {
            safeLog.error('❌ Error fetching clinic Bill config:', error);
            throw new Error(`Failed to fetch clinic config: ${error.message}`);
          }

          if (!data) {
            throw new Error('No clinic configuration found');
          }

          safeLog.debug('✅ Clinic Bill config fetched successfully');
          return data as CachedBillConfig;
        },
        cacheService.getConfig('BILL_CONFIG')
      );
    } catch (error) {
      safeLog.error(`❌ BillConfigCacheService: Error getting clinic config for ${clinicId}:`, error);
      return null;
    }
  }

  /**
   * Invalidate Bill config cache for specific user
   */
  async invalidateUserConfigCache(userId: string): Promise<void> {
    const key = `${this.CONFIG_KEY_PREFIX}user:${userId}`;
    await cacheService.invalidate(key);
    safeLog.debug(`🗑️ Invalidated Bill config cache for user: ${userId}`);
  }

  /**
   * Invalidate clinic config cache
   */
  async invalidateClinicConfigCache(clinicId: string): Promise<void> {
    const key = `${this.CONFIG_KEY_PREFIX}clinic:${clinicId}`;
    await cacheService.invalidate(key);
    safeLog.debug(`🗑️ Invalidated Bill config cache for clinic: ${clinicId}`);
  }

  /**
   * Invalidate admin default config cache
   */
  async invalidateAdminConfigCache(): Promise<void> {
    const key = `${this.CONFIG_KEY_PREFIX}admin:default`;
    await cacheService.invalidate(key);
    safeLog.debug('🗑️ Invalidated admin default Bill config cache');
  }

  /**
   * Invalidate all Bill config caches
   */
  async invalidateAllConfigCaches(): Promise<void> {
    try {
      await cacheService.invalidatePattern(this.CONFIG_KEY_PREFIX);
      safeLog.debug('🗑️ BillConfigCacheService: Invalidated all Bill config caches');
    } catch (error) {
      safeLog.error('❌ BillConfigCacheService: Error invalidating all config caches:', error);
      throw error;
    }
  }

  /**
   * Invalidate all caches related to a specific user's Bill configuration
   */
  async invalidateUserBillCaches(userId: string): Promise<void> {
    if (!userId) {
      safeLog.warn('⚠️ BillConfigCacheService: No userId provided for cache invalidation');
      return;
    }

    try {
      safeLog.debug(`🗑️ BillConfigCacheService: Invalidating Bill config cache for user: ${userId.substring(0, 8)}...`);
      
      await this.invalidateUserConfigCache(userId);
      
      safeLog.debug(`✅ BillConfigCacheService: User Bill config cache invalidated for: ${userId.substring(0, 8)}...`);
    } catch (error) {
      safeLog.error(`❌ BillConfigCacheService: Error invalidating user Bill caches for ${userId.substring(0, 8)}...:`, {
        error: error instanceof Error ? error.message : String(error),
        timestamp: new Date().toISOString()
      });
      throw error;
    }
  }

  /**
   * Preload Bill configuration for better performance
   */
  async preloadBillConfigForUser(userId: string): Promise<void> {
    try {
      safeLog.debug(`🚀 Preloading Bill config for user: ${userId}`);
      
      const config = await this.getBillConfigForUser(userId);
      
      if (config) {
        safeLog.debug(`✅ Preloaded Bill config: ${config.personality_type}`);
      } else {
        safeLog.debug('ℹ️ No Bill config to preload');
      }
    } catch (error) {
      safeLog.error('❌ Error preloading Bill config:', error);
    }
  }

  /**
   * Get formatted configuration for API calls (backward compatibility)
   */
  async getFormattedConfigForAPI(userId: string): Promise<any> {
    const config = await this.getBillConfigForUser(userId);
    
    if (!config) {
      return {
        system_prompt: null,
        personality_type: 'empathetic',
        clinic_info: null,
        knowledge_base: null,
        sobriety_congrats: true
      };
    }

    return {
      system_prompt: config.system_prompt,
      personality_type: config.personality_type,
      clinic_info: config.clinic_info,
      knowledge_base: config.knowledge_base,
      sobriety_congrats: config.sobriety_congrats
    };
  }
}

// Export singleton instance
export const billConfigCacheService = new BillConfigCacheService();
export default billConfigCacheService;
